import { useMutation } from '@tanstack/react-query';
import { useNavigateKeepSearch } from '@hooks/useNavigateKeepSearch';
import { completeEntraIdSetup } from '../../api/portalServices';
import { getTenantIdFromUrl } from '../../api/utils';
import { useSession } from '../../common/authorization/context/Session';
import { useErrorContext } from '../../errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '../../errorHandler/errors';
import {
  EamcPath,
  ENTRA_SETUP_ORIGIN_KEY,
  isRouteArea,
  type RouteArea,
  SettingsPath,
} from '../constants';
import { useEntraIdInitialSync } from '@imprivata-cloud/core';

const USERS_PAGE_OF: Record<RouteArea, SettingsPath | EamcPath> = {
  global: SettingsPath.ENTRA_ID_USERS,
  eamc: EamcPath.ENTRA_ID_USERS,
};

const readOrigin = (): string | undefined =>
  document.cookie
    .split('; ')
    .find(cookie => cookie.startsWith(`${ENTRA_SETUP_ORIGIN_KEY}=`))
    ?.split('=')[1];

// Microsoft returns to the tenant-agnostic host, so the origin cookie is scoped to it.
export const recordSetupOrigin = (area: RouteArea) => {
  const { hostname } = window.location;
  const domain = getTenantIdFromUrl(hostname)?.urlWithoutTenantId ?? hostname;
  // biome-ignore lint/suspicious/noDocumentCookie: some supported browsers lack Cookie Store
  document.cookie = `${ENTRA_SETUP_ORIGIN_KEY}=${area}; domain=${domain}; path=/`;
};

const setupOrigin = (): RouteArea => {
  const origin = readOrigin();
  if (isRouteArea(origin)) {
    return origin;
  }
  if (origin !== undefined) {
    console.warn('[entra-id-setup] unknown setup origin, returning to settings', origin);
  }
  return 'global';
};

const useEntraIdSetupComplete = () => {
  const { emitError } = useErrorContext();
  const { tenantId } = useSession();
  const navigateKeepSearch = useNavigateKeepSearch();
  const returnToOrigin = () => navigateKeepSearch(USERS_PAGE_OF[setupOrigin()]);
  const { mutate: startInitialSync } = useEntraIdInitialSync({
    onSuccess: () => {
      console.log('Entra ID initial sync completed');
      returnToOrigin();
    },
    onError: (e: Error) => {
      console.log('Entra ID initial sync failed', e);
      const apiError = e as unknown as ApiError;
      emitError(new AppError(apiError));
    },
  });

  return useMutation({
    mutationFn: async (tenantId: string) => {
      await completeEntraIdSetup(tenantId);
    },
    onSuccess: async () => {
      console.log('Entra ID setup completed');
      startInitialSync(tenantId || '');
    },
    onError: (e: Error) => {
      console.log('Entra ID setup failed', e);
      const apiError = e as unknown as ApiError;
      emitError(new AppError(apiError));
      returnToOrigin();
    },
  });
};

export default useEntraIdSetupComplete;
